import { useEffect, useMemo } from "react";
import PropTypes from "prop-types";
import { motion } from "framer-motion";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  Person,
  CalendarToday,
  Business,
  Work,
  AttachMoney,
  AccountBalance,
  TrendingUp,
  TrendingDown,
  Calculate,
  CurrencyExchange,
} from "@mui/icons-material";
import Input from "../../../components/ui/Input";
import Button from "../../../components/ui/Button";
import Card from "../../../components/ui/Card";

const validationSchema = Yup.object({
  employee_id: Yup.string().required("Employee is required"),
  month: Yup.string()
    .matches(/^\d{4}-(0[1-9]|1[0-2])$/, "Select a valid month")
    .nullable()
    .transform((value, originalValue) => {
      // Convert empty string to null
      return originalValue === "" ? null : value;
    }),
  currency: Yup.string().oneOf(["INR", "USD"]).default("INR"),
  basic_salary: Yup.number().min(0).required("Basic salary is required"),
  hra: Yup.number().min(0).required("HRA is required"),
  allowances: Yup.number().min(0).required("Allowances are required"),
  overtime_amount: Yup.number().min(0).required("Overtime is required"),
  professional_tax: Yup.number()
    .min(0)
    .required("Professional tax is required"),
  pf_contribution: Yup.number().min(0).required("PF contribution is required"),
  esi: Yup.number().min(0).required("ESI is required"),
  tds: Yup.number().min(0).required("TDS is required"),
  bank_account_no: Yup.string()
    .min(6, "Enter a valid account number")
    .required("Bank account is required"),
  ifsc_code: Yup.string()
    .min(4, "Enter a valid IFSC code")
    .required("IFSC is required"),
});

const numberOrZero = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

function SalarySlipForm({
  initialValues,
  employees,
  onSubmit,
  onCancel,
  submitLabel = "Create Salary Slip",
  loading = false,
}) {
  const formik = useFormik({
    initialValues,
    enableReinitialize: true,
    validationSchema,
    onSubmit: async (values, helpers) => {
      const payload = buildPayload(values);
      try {
        await onSubmit(payload);
      } finally {
        helpers.setSubmitting(false);
      }
    },
  });

  const selectedEmployee = useMemo(
    () => employees.find((emp) => emp.id === formik.values.employee_id) || null,
    [employees, formik.values.employee_id]
  );

  const summary = useMemo(() => {
    const gross_salary =
      numberOrZero(formik.values.basic_salary) +
      numberOrZero(formik.values.hra) +
      numberOrZero(formik.values.allowances) +
      numberOrZero(formik.values.overtime_amount);

    const total_deductions =
      numberOrZero(formik.values.professional_tax) +
      numberOrZero(formik.values.pf_contribution) +
      numberOrZero(formik.values.esi) +
      numberOrZero(formik.values.tds);

    const net_salary = gross_salary - total_deductions;

    return {
      gross_salary,
      total_deductions,
      net_salary,
    };
  }, [formik.values]);

  useEffect(() => {
    if (!selectedEmployee) {
      return;
    }

    const updates = {
      employee_name: selectedEmployee.name || "",
      designation: selectedEmployee.designation || "",
      department: selectedEmployee.department || "",
      bank_account_no:
        selectedEmployee.bank_account_no || formik.values.bank_account_no,
      ifsc_code: selectedEmployee.ifsc_code || formik.values.ifsc_code,
    };

    Object.entries(updates).forEach(([field, value]) => {
      if (value && formik.values[field] !== value) {
        formik.setFieldValue(field, value, false);
      }
    });
  }, [selectedEmployee]);

  return (
    <form onSubmit={formik.handleSubmit} noValidate>
      {/* Employee & Period */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
      >
        <Card className="mb-6">
          <h3 className="text-xl font-bold text-primary mb-6 pb-4 border-b border-gray-200">
            Employee & Period
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <Person className="w-5 h-5 text-gray-400" />
                <label className="text-sm font-semibold text-gray-700">
                  Employee
                </label>
              </div>
              <select
                id="employee_id"
                name="employee_id"
                value={formik.values.employee_id}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                className={`input-field w-full ${
                  formik.touched.employee_id && formik.errors.employee_id
                    ? "border-red-500"
                    : ""
                }`}
              >
                <option value="">Select Employee</option>
                {employees.map((employee) => (
                  <option key={employee.id} value={employee.id}>
                    {employee.name} — {employee.designation || "Employee"}
                  </option>
                ))}
              </select>
              {formik.touched.employee_id && formik.errors.employee_id && (
                <p className="text-sm text-red-600 mt-1">
                  {formik.errors.employee_id}
                </p>
              )}
            </div>

            <div>
              <div className="flex items-center gap-2 mb-2">
                <CalendarToday className="w-5 h-5 text-gray-400" />
                <label className="text-sm font-semibold text-gray-700">
                  Salary Month <span className="text-gray-400 font-normal text-xs">(Optional)</span>
                </label>
              </div>
              <Input
                id="month"
                name="month"
                type="month"
                value={formik.values.month || ""}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={
                  formik.touched.month && formik.errors.month
                    ? formik.errors.month
                    : ""
                }
                placeholder="Leave empty for general template"
              />
              <p className="text-xs text-gray-500 mt-1">
                Leave empty for general salary slip. Only specify month for bonuses or custom adjustments.
              </p>
            </div>

            <div>
              <div className="flex items-center gap-2 mb-2">
                <CurrencyExchange className="w-5 h-5 text-gray-400" />
                <label className="text-sm font-semibold text-gray-700">
                  Currency
                </label>
              </div>
              <select
                id="currency"
                name="currency"
                value={formik.values.currency || "INR"}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                className={`input-field w-full ${
                  formik.touched.currency && formik.errors.currency
                    ? "border-red-500"
                    : ""
                }`}
              >
                <option value="INR">INR (Indian Rupee)</option>
                <option value="USD">USD (US Dollar)</option>
              </select>
              {formik.touched.currency && formik.errors.currency && (
                <p className="text-sm text-red-600 mt-1">
                  {formik.errors.currency}
                </p>
              )}
            </div>

            <div>
              <div className="flex items-center gap-2 mb-2">
                <Person className="w-5 h-5 text-gray-400" />
                <label className="text-sm font-semibold text-gray-700">
                  Employee Name
                </label>
              </div>
              <Input
                id="employee_name"
                name="employee_name"
                type="text"
                value={formik.values.employee_name}
                readOnly
                className="bg-gray-50"
              />
            </div>

            <div>
              <div className="flex items-center gap-2 mb-2">
                <Work className="w-5 h-5 text-gray-400" />
                <label className="text-sm font-semibold text-gray-700">
                  Designation
                </label>
              </div>
              <Input
                id="designation"
                name="designation"
                type="text"
                value={formik.values.designation}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={
                  formik.touched.designation && formik.errors.designation
                    ? formik.errors.designation
                    : ""
                }
                placeholder="Enter designation"
              />
            </div>

            <div>
              <div className="flex items-center gap-2 mb-2">
                <Business className="w-5 h-5 text-gray-400" />
                <label className="text-sm font-semibold text-gray-700">
                  Department
                </label>
              </div>
              <Input
                id="department"
                name="department"
                type="text"
                value={formik.values.department}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={
                  formik.touched.department && formik.errors.department
                    ? formik.errors.department
                    : ""
                }
                placeholder="Enter department"
              />
            </div>
          </div>
        </Card>
      </motion.div>

      {/* Earnings */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
      >
        <Card className="mb-6">
          <div className="flex items-center gap-3 mb-6 pb-4 border-b border-gray-200">
            <TrendingUp className="w-6 h-6 text-green-500" />
            <h3 className="text-xl font-bold text-primary">Earnings</h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <AttachMoney className="w-5 h-5 text-gray-400" />
                <label className="text-sm font-semibold text-gray-700">
                  Basic Salary
                </label>
              </div>
              <Input
                id="basic_salary"
                name="basic_salary"
                type="number"
                value={formik.values.basic_salary}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={
                  formik.touched.basic_salary && formik.errors.basic_salary
                    ? formik.errors.basic_salary
                    : ""
                }
                placeholder="0.00"
              />
            </div>

            <div>
              <div className="flex items-center gap-2 mb-2">
                <AttachMoney className="w-5 h-5 text-gray-400" />
                <label className="text-sm font-semibold text-gray-700">
                  HRA
                </label>
              </div>
              <Input
                id="hra"
                name="hra"
                type="number"
                value={formik.values.hra}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={
                  formik.touched.hra && formik.errors.hra
                    ? formik.errors.hra
                    : ""
                }
                placeholder="0.00"
              />
            </div>

            <div>
              <div className="flex items-center gap-2 mb-2">
                <AttachMoney className="w-5 h-5 text-gray-400" />
                <label className="text-sm font-semibold text-gray-700">
                  Allowances
                </label>
              </div>
              <Input
                id="allowances"
                name="allowances"
                type="number"
                value={formik.values.allowances}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={
                  formik.touched.allowances && formik.errors.allowances
                    ? formik.errors.allowances
                    : ""
                }
                placeholder="0.00"
              />
            </div>

            <div>
              <div className="flex items-center gap-2 mb-2">
                <AttachMoney className="w-5 h-5 text-gray-400" />
                <label className="text-sm font-semibold text-gray-700">
                  Overtime Amount
                </label>
              </div>
              <Input
                id="overtime_amount"
                name="overtime_amount"
                type="number"
                value={formik.values.overtime_amount}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={
                  formik.touched.overtime_amount &&
                  formik.errors.overtime_amount
                    ? formik.errors.overtime_amount
                    : ""
                }
                placeholder="0.00"
              />
            </div>
          </div>
        </Card>
      </motion.div>

      {/* Deductions */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
      >
        <Card className="mb-6">
          <div className="flex items-center gap-3 mb-6 pb-4 border-b border-gray-200">
            <TrendingDown className="w-6 h-6 text-red-500" />
            <h3 className="text-xl font-bold text-primary">Deductions</h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <AttachMoney className="w-5 h-5 text-gray-400" />
                <label className="text-sm font-semibold text-gray-700">
                  Professional Tax
                </label>
              </div>
              <Input
                id="professional_tax"
                name="professional_tax"
                type="number"
                value={formik.values.professional_tax}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={
                  formik.touched.professional_tax &&
                  formik.errors.professional_tax
                    ? formik.errors.professional_tax
                    : ""
                }
                placeholder="0.00"
              />
            </div>

            <div>
              <div className="flex items-center gap-2 mb-2">
                <AttachMoney className="w-5 h-5 text-gray-400" />
                <label className="text-sm font-semibold text-gray-700">
                  PF Contribution
                </label>
              </div>
              <Input
                id="pf_contribution"
                name="pf_contribution"
                type="number"
                value={formik.values.pf_contribution}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={
                  formik.touched.pf_contribution &&
                  formik.errors.pf_contribution
                    ? formik.errors.pf_contribution
                    : ""
                }
                placeholder="0.00"
              />
            </div>

            <div>
              <div className="flex items-center gap-2 mb-2">
                <AttachMoney className="w-5 h-5 text-gray-400" />
                <label className="text-sm font-semibold text-gray-700">
                  ESI
                </label>
              </div>
              <Input
                id="esi"
                name="esi"
                type="number"
                value={formik.values.esi}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={
                  formik.touched.esi && formik.errors.esi
                    ? formik.errors.esi
                    : ""
                }
                placeholder="0.00"
              />
            </div>

            <div>
              <div className="flex items-center gap-2 mb-2">
                <AttachMoney className="w-5 h-5 text-gray-400" />
                <label className="text-sm font-semibold text-gray-700">
                  TDS
                </label>
              </div>
              <Input
                id="tds"
                name="tds"
                type="number"
                value={formik.values.tds}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={
                  formik.touched.tds && formik.errors.tds
                    ? formik.errors.tds
                    : ""
                }
                placeholder="0.00"
              />
            </div>
          </div>
        </Card>
      </motion.div>

      {/* Bank & Summary */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
      >
        <Card className="mb-6">
          <div className="flex items-center gap-3 mb-6 pb-4 border-b border-gray-200">
            <AccountBalance className="w-6 h-6 text-primary" />
            <h3 className="text-xl font-bold text-primary">Bank & Summary</h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <AccountBalance className="w-5 h-5 text-gray-400" />
                <label className="text-sm font-semibold text-gray-700">
                  Bank Account Number
                </label>
              </div>
              <Input
                id="bank_account_no"
                name="bank_account_no"
                type="text"
                value={formik.values.bank_account_no}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={
                  formik.touched.bank_account_no &&
                  formik.errors.bank_account_no
                    ? formik.errors.bank_account_no
                    : ""
                }
                placeholder="Enter account number"
              />
            </div>

            <div>
              <div className="flex items-center gap-2 mb-2">
                <AccountBalance className="w-5 h-5 text-gray-400" />
                <label className="text-sm font-semibold text-gray-700">
                  IFSC Code
                </label>
              </div>
              <Input
                id="ifsc_code"
                name="ifsc_code"
                type="text"
                value={formik.values.ifsc_code}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={
                  formik.touched.ifsc_code && formik.errors.ifsc_code
                    ? formik.errors.ifsc_code
                    : ""
                }
                placeholder="Enter IFSC code"
              />
            </div>
          </div>

          {/* Summary */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 pt-6 border-t border-gray-200">
            <div className="p-4 bg-gradient-to-br from-green-50 to-emerald-50 rounded-2xl border border-green-200">
              <div className="flex items-center gap-2 mb-2">
                <Calculate className="w-5 h-5 text-green-600" />
                <label className="text-sm font-semibold text-gray-700">
                  Gross Salary
                </label>
              </div>
              <p className="text-2xl font-bold text-green-600">
                {formik.values.currency === "USD" ? "$" : "₹"}
                {summary.gross_salary.toLocaleString(
                  formik.values.currency === "USD" ? "en-US" : "en-IN",
                  {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  }
                )}
              </p>
            </div>

            <div className="p-4 bg-gradient-to-br from-red-50 to-rose-50 rounded-2xl border border-red-200">
              <div className="flex items-center gap-2 mb-2">
                <Calculate className="w-5 h-5 text-red-600" />
                <label className="text-sm font-semibold text-gray-700">
                  Total Deductions
                </label>
              </div>
              <p className="text-2xl font-bold text-red-600">
                {formik.values.currency === "USD" ? "$" : "₹"}
                {summary.total_deductions.toLocaleString(
                  formik.values.currency === "USD" ? "en-US" : "en-IN",
                  {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  }
                )}
              </p>
            </div>

            <div className="p-4 bg-gradient-to-br from-primary/10 to-secondary/10 rounded-2xl border-2 border-primary/20">
              <div className="flex items-center gap-2 mb-2">
                <Calculate className="w-5 h-5 text-primary" />
                <label className="text-sm font-semibold text-primary">
                  Net Salary
                </label>
              </div>
              <p className="text-2xl font-bold text-primary">
                {formik.values.currency === "USD" ? "$" : "₹"}
                {summary.net_salary.toLocaleString(
                  formik.values.currency === "USD" ? "en-US" : "en-IN",
                  {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  }
                )}
              </p>
            </div>
          </div>
        </Card>
      </motion.div>

      {/* Submit Buttons */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5 }}
        className="flex justify-end gap-4"
      >
        <Button
          variant="secondary"
          onClick={onCancel}
          disabled={formik.isSubmitting || loading}
          className="min-w-[120px]"
        >
          Cancel
        </Button>
        <Button
          type="submit"
          variant="primary"
          disabled={formik.isSubmitting || loading}
          className="min-w-[200px]"
        >
          {formik.isSubmitting || loading ? "Processing..." : submitLabel}
        </Button>
      </motion.div>
    </form>
  );
}

const buildPayload = (values) => ({
  employee_id: values.employee_id,
  employee_name: values.employee_name,
  designation: values.designation,
  department: values.department,
  month: values.month || null, // Send null if empty for general template
  currency: values.currency || "INR",
  basic_salary: numberOrZero(values.basic_salary),
  hra: numberOrZero(values.hra),
  allowances: numberOrZero(values.allowances),
  overtime_amount: numberOrZero(values.overtime_amount),
  professional_tax: numberOrZero(values.professional_tax),
  pf_contribution: numberOrZero(values.pf_contribution),
  esi: numberOrZero(values.esi),
  tds: numberOrZero(values.tds),
  bank_account_no: values.bank_account_no,
  ifsc_code: values.ifsc_code,
});

SalarySlipForm.propTypes = {
  initialValues: PropTypes.object.isRequired,
  employees: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      designation: PropTypes.string,
      department: PropTypes.string,
      bank_account_no: PropTypes.string,
      ifsc_code: PropTypes.string,
      code: PropTypes.string,
      email: PropTypes.string,
    })
  ).isRequired,
  onSubmit: PropTypes.func.isRequired,
  onCancel: PropTypes.func,
  submitLabel: PropTypes.string,
  loading: PropTypes.bool,
};

export default SalarySlipForm;
